import React, { useState, useRef } from "react";
import AppFormInput from '../../../../components/ui/AppFormInput';
import { Toast } from 'primereact/toast';
import { Panel } from 'primereact/panel';
import apiClient from "../../../../services/api-client";
import { Divider } from 'primereact/divider';
import { MultiSelect } from 'primereact/multiselect';
import { Skeleton } from 'primereact/skeleton';
import { useMountEffect } from "primereact/hooks";

import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import StorageHelper from '../../../../utils/storage-helper';

const authHeaders = () => ({ Authorization: 'Bearer ' + StorageHelper.get('token') });

// Groups the role's existing permissions under the function group each one belongs to.
const groupExistingPermissions = (groups, rolePermissions) =>
  groups
    .map((group) => ({
      functionName: group.groupName,
      permissions: rolePermissions.filter((p) => group.systemFunctions.some((sf) => sf.id === p.id)),
    }))
    .filter((entry) => entry.permissions.length > 0);

const RoleFormSkeleton = () => (
  <div className="border-round border-1 surface-border p-4 surface-card">
    <div className="flex mb-3">
      <Skeleton shape="circle" size="4rem" className="mr-2"></Skeleton>
      <div>
        <Skeleton width="10rem" className="mb-2"></Skeleton>
        <Skeleton width="5rem" className="mb-2"></Skeleton>
        <Skeleton height=".5rem"></Skeleton>
      </div>
    </div>
    <Skeleton width="100%" height="150px"></Skeleton>
    <div className="flex justify-content-between mt-3">
      <Skeleton width="4rem" height="2rem"></Skeleton>
      <Skeleton width="4rem" height="2rem"></Skeleton>
    </div>
  </div>
);

/**
 * Create or edit a role and its permissions. Pass `roleData` to edit an existing role.
 */
const RoleForm = ({ roleData = null }) => {
  const isEdit = roleData !== null;
  const toast = useRef(null);
  const [groupedRoles, setGroupedRoles] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState(null);
  const [openPanel, setOpenPanel] = useState(null);
  const [confirmedPermissions, setConfirmedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleName, setRoleName] = useState(roleData?.name ?? "");
  const [roleDescription, setRoleDescription] = useState(roleData?.description ?? "");

  useMountEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await apiClient.get('system/functions/groups', { headers: authHeaders() });
        const grouped = response.data.responseBody;
        setGroupedRoles(grouped);

        if (isEdit && roleData.permissions && grouped) {
          setConfirmedPermissions(groupExistingPermissions(grouped, roleData.permissions));
        }
      } catch (e) {
        console.error('Failed to load permission groups:', e);
      }
    };
    fetchGroups();
  });

  const handleRoleChange = (groupName, value) => {
    setSelectedPermissions(value);

    setConfirmedPermissions((previous) => {
      const exists = previous.some((element) => element.functionName === groupName);
      if (!exists) {
        return [...previous, { functionName: groupName, permissions: value }];
      }
      return previous.map((element) =>
        element.functionName === groupName ? { ...element, permissions: value } : element
      );
    });
  };

  const handlePanelToggle = (groupName) => {
    const confirmed = confirmedPermissions.find((element) => element.functionName === groupName);
    setSelectedPermissions(confirmed ? confirmed.permissions : null);
    setOpenPanel((prevOpenPanel) => (prevOpenPanel === groupName ? null : groupName));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // The create endpoint takes permission ids as strings, the edit endpoint as numbers.
    const toFunctionId = isEdit ? (id) => id : (id) => id + "";
    const payload = {
      ...(isEdit && { id: roleData.id }),
      roleName,
      roleDescription,
      systemFunctions: confirmedPermissions.flatMap((item) => item.permissions.map((permission) => toFunctionId(permission.id)))
    };

    try {
      const response = await apiClient.post('system/roles/add',
        { "accessToken": "", "operation": "set-new-password", "requestBody": payload },
        { headers: authHeaders() });

      if (response.data.status === "SUCCESS" && response.data.responseBody != null) {
        window.location.reload();
      } else {
        toast.current.show({
          severity: 'error',
          summary: 'Failed to create a new role',
          detail: response.data.message,
          life: 4000
        });
      }
    } catch (error) {
      console.error('Error creating new role:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPanelHeader = (role) => {
    const confirmed = confirmedPermissions.find((p) => p.functionName === role.groupName);
    const count = confirmed?.permissions ? confirmed.permissions.length : 0;

    return (
      <div className="flex items-center gap-2">
        <span>{role.groupName}</span>
        {count > 0 && (
          <span className="badge badge-outline badge-sm text-xs">{count} selected</span>
        )}
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />
      {loading ? <RoleFormSkeleton /> : (
        <div className="">
          <div className="container">
            <div className="row">
              <div className="">
                <form onSubmit={handleSubmit}>
                  <AppFormInput label="Role Name" id="roleName" value={roleName} onChange={(e) => setRoleName(e.target.value)} />

                  <AppFormInput label="Role Description" id="roleDescription" required={!isEdit} value={roleDescription} onChange={(e) => setRoleDescription(e.target.value)} />

                  <Divider className="p-divider-center"> <small>Assign Functions that applies</small></Divider>

                  {groupedRoles.map((role) => (
                    <div className="card" key={role.groupName}>
                      <Panel
                        header={getPanelHeader(role)}
                        toggleable
                        collapsed={openPanel !== role.groupName}
                        className="panel-header"
                        onToggle={() => handlePanelToggle(role.groupName)}
                      >
                        <div className="card-body flex justify-content-center">
                          <span className="p-float-label">
                            <MultiSelect
                              size="small"
                              value={selectedPermissions}
                              filter
                              onChange={(e) => handleRoleChange(role.groupName, e.value)}
                              options={role.systemFunctions}
                              optionLabel="name"
                              display="chip"
                              placeholder="Select permissions"
                              maxSelectedLabels={3}
                              className="w-full md:w-20rem create-role-input"
                            />
                            <label style={{ paddingLeft: "5px", paddingRight: "5px" }} htmlFor={`permissionName${role.groupName}`}>Permissions</label>
                          </span>
                        </div>
                      </Panel>
                    </div>
                  ))}
                  <div className="mt-6 flex justify-end pt-4 border-t border-base-200">
                    <button type="button" onClick={handleSubmit} className="btn btn-primary px-8 text-white rounded-lg shadow-sm">
                      <i className="pi pi-check"></i> {isEdit ? 'Save Changes' : 'Create Role'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RoleForm;
